import React from 'react';
import { observer } from 'mobx-react';

import { injectStores } from 'app/util/injectStores';

import type PagedCollection from 'core/model/PagedCollection';
import Paginator from 'core/components/Paginator';

import { Box, Text, Flex, Spinner, Select } from 'core/components';

interface Pagination {
  onPageChange: (params: { page: number; perPage?: number; refetchTotals?: boolean }) => void;
  perPage?: number;
  perPageOptions?: number[];
  persistKey?: string;
  hidePerPage?: boolean;
}

interface SetupStore {
  updateSettings: (settings: Record<string, unknown>) => void;
  getSettings: (path: string, defaultValue?: unknown) => Record<string, unknown>;
}

interface Props {
  collection: PagedCollection;
  pagination: Pagination;
  $setup: SetupStore;
}

function AdminTablePaginator({ collection, pagination, $setup }: Props) {
  if (!pagination || !collection.pageState) {
    return null;
  }

  const { currentPage, perPage, pageCount, pageCountLoaded, totalCount } = collection.pageState;
  const {
    persistKey,
    onPageChange,
    perPageOptions = [20, 50, 100, 250],
    perPage: defaultPerPage = 20,
    hidePerPage
  } = pagination;

  const handlePageChange = (page: number) => {
    collection.setPage(page);
    onPageChange({ page, refetchTotals: false });
  };

  const handlePerPageChange = (value: number) => {
    collection.setPageState({ perPage: value, currentPage: 1, offset: 0 });
    onPageChange({ page: 1, perPage: value, refetchTotals: true });

    if (persistKey) {
      $setup.updateSettings({
        tablePaginationSettings: {
          ...$setup.getSettings('tablePaginationSettings', {}),
          [persistKey]: { perPage: value }
        }
      });
    }
  };

  const effectivePerPageValue = perPage ?? defaultPerPage;

  // If the perPage value is not an option (ex: outdated user persisted value), add it & re-sort
  const effectivePerPageOptions =
    effectivePerPageValue > 0 && !perPageOptions.includes(effectivePerPageValue)
      ? [...perPageOptions, effectivePerPageValue].sort((a, b) => a - b)
      : perPageOptions;

  return (
    <Flex minHeight={40} alignItems="center" gap={1}>
      {!pageCountLoaded && (
        <>
          <Spinner size={16} />
          <Text small muted>
            Loading pages...
          </Text>
        </>
      )}

      {pageCountLoaded && totalCount > 0 && (
        <>
          {collection.requestStatus === 'fetching' && <Spinner size={16} />}
          <Text small mr="4px">
            {collection.requestStatus === 'fetching' ? 'Loading' : 'Showing'}{' '}
            {((currentPage - 1) * perPage + 1).toLocaleString()}–
            {Math.min(currentPage * perPage, totalCount).toLocaleString()} of {Number(totalCount).toLocaleString()}
          </Text>
          <Paginator
            hideFirstAndLastPageLinks
            currentPage={Math.min(currentPage ?? 1, pageCount ?? 1)}
            totalPages={pageCount ?? 1}
            onChange={handlePageChange}
          />
        </>
      )}

      {pageCountLoaded && totalCount === 0 && (
        <Text small muted>
          Showing 0 results
        </Text>
      )}

      {!hidePerPage && (
        <Box ml={!pageCountLoaded || totalCount === 0 ? '4px' : 0}>
          <Select
            height={30}
            menuWidth={120}
            minWidth={120}
            onChange={handlePerPageChange}
            options={effectivePerPageOptions.map((v: number) => ({
              label: `${v} Per Page`,
              value: v
            }))}
            renderAsButton
            small
            toggle
            values={effectivePerPageValue}
            width="auto"
          />
        </Box>
      )}
    </Flex>
  );
}

export default injectStores('$setup')(observer(AdminTablePaginator));
